import React, { useEffect, useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import {
    adminMarkAttendance,
    attendancelist,
    attendancestafflist,
} from "../../../services/attendance";

const today = moment().format("YYYY-MM-DD");

const TYPES = [
    { value: "present", label: "Present", color: "#28a745" },
    { value: "absent", label: "Absent", color: "#dc3545" },
    { value: "leave", label: "Leave", color: "#ffc107" },
    { value: "on_duty", label: "On Duty", color: "#17a2b8" },
    { value: "lop", label: "LOP", color: "#e67e22" },
];

const TYPE_LABEL = TYPES.reduce((a, t) => ({ ...a, [t.value]: t.label }), {});

export const MarkAttendance = () => {
    const [staff, setStaff] = useState([]);
    const [marked, setMarked] = useState({});

    const [date, setDate] = useState(today);
    const [userId, setUserId] = useState("");
    const [type, setType] = useState("");
    const [clockIn, setClockIn] = useState("");
    const [clockOut, setClockOut] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const branch = localStorage.getItem("branch");
        attendancestafflist({ branch }).then((res) => {
            if (res && res.status === true) setStaff(res.data.staff);
        });
        loadMarked(today);
    }, []);

    // Existing attendance for the chosen date, keyed by user id
    const loadMarked = async (targetDate) => {
        const res = await attendancelist({
            date: targetDate,
            branch: localStorage.getItem("branch"),
            per_page: 1000,
        });
        const map = {};
        if (res && res.status === true) {
            (res.data.attendances.data || []).forEach((a) => {
                map[a.user_id] = a;
            });
        }
        setMarked(map);
    };

    function changeDate(val) {
        if (val > today) {
            toast.info("Cannot mark attendance for a future date");
            return;
        }
        setDate(val);
        loadMarked(val);
    }

    function changeType(val) {
        setType(val);
        if (!["present", "on_duty"].includes(val)) {
            setClockIn("");
            setClockOut("");
        }
    }

    function resetForm() {
        setUserId("");
        setType("");
        setClockIn("");
        setClockOut("");
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!userId) {
            toast.info("Please select a staff");
            return;
        }
        if (!type) {
            toast.info("Please select an attendance type");
            return;
        }
        if (clockIn && clockOut && clockOut <= clockIn) {
            toast.error("Clock out must be after clock in");
            return;
        }

        setSaving(true);
        const res = await adminMarkAttendance({
            date,
            branch: localStorage.getItem("branch"),
            entries: [
                {
                    user_id: Number(userId),
                    attendance_type: type,
                    clock_in: clockIn || null,
                    clock_out: clockOut || null,
                },
            ],
        });
        setSaving(false);

        if (res && res.status === true) {
            const skipped = res.data?.skipped || [];
            if (skipped.length > 0) {
                toast.info(skipped[0].reason);
            } else {
                toast.success(res.message);
                resetForm();
            }
            loadMarked(date);
        } else if (res) {
            toast.info(res.message);
        }
    }

    const timesEnabled = ["present", "on_duty"].includes(type);
    const existing = userId ? marked[userId] : null;
    const markedList = staff.filter((s) => marked[s.id]);

    return (
        <div className="app-content main-content">
            <div className="side-app main-container">
                <div className="page-header d-xl-flex d-block">
                    <div className="page-leftheader">
                        <div className="page-title">Mark Attendance</div>
                    </div>
                </div>

                <div className="row">
                    <div className="col-xl-7">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">Staff Attendance</h4>
                            </div>
                            <div className="card-body">
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label className="form-label">
                                            Date <span className="text-danger">*</span>
                                        </label>
                                        <input
                                            type="date"
                                            className="form-control"
                                            value={date}
                                            max={today}
                                            onChange={(e) => changeDate(e.target.value)}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Staff <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-control"
                                            value={userId}
                                            onChange={(e) => setUserId(e.target.value)}
                                        >
                                            <option value="">-- Select Staff --</option>
                                            {staff.map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name}
                                                    {marked[s.id]
                                                        ? ` (${TYPE_LABEL[marked[s.id].attendance_type] ||
                                                          marked[s.id].attendance_type})`
                                                        : ""}
                                                </option>
                                            ))}
                                        </select>
                                        {existing && (
                                            <small className="text-warning d-block mt-1">
                                                Already marked as{" "}
                                                <b>
                                                    {TYPE_LABEL[existing.attendance_type] ||
                                                        existing.attendance_type}
                                                </b>{" "}
                                                on {moment(date).format("DD/MM/YYYY")}. Submitting
                                                will update it.
                                            </small>
                                        )}
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            Attendance Type <span className="text-danger">*</span>
                                        </label>
                                        <select
                                            className="form-control"
                                            value={type}
                                            onChange={(e) => changeType(e.target.value)}
                                        >
                                            <option value="">-- Select Type --</option>
                                            {TYPES.map((t) => (
                                                <option key={t.value} value={t.value}>
                                                    {t.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    {timesEnabled && (
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Clock In</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={clockIn}
                                                        onChange={(e) => setClockIn(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group">
                                                    <label className="form-label">Clock Out</label>
                                                    <input
                                                        type="time"
                                                        className="form-control"
                                                        value={clockOut}
                                                        onChange={(e) => setClockOut(e.target.value)}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <div className="text-end mt-3">
                                        <button
                                            type="button"
                                            className="btn btn-secondary me-2"
                                            onClick={resetForm}
                                            disabled={saving}
                                        >
                                            Reset
                                        </button>
                                        <button
                                            type="submit"
                                            className="btn btn-primary"
                                            disabled={saving}
                                        >
                                            {saving ? "Saving..." : "Submit"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-5">
                        <div className="card">
                            <div className="card-header">
                                <h4 className="card-title">
                                    Marked on {moment(date).format("DD/MM/YYYY")}
                                </h4>
                                <span className="ms-auto text-muted">
                                    {markedList.length} of {staff.length}
                                </span>
                            </div>
                            <div className="card-body">
                                <div className="table-responsive">
                                    <table className="table table-bordered table-sm align-middle">
                                        <thead>
                                            <tr>
                                                <th style={{ width: "55px" }}>S.No</th>
                                                <th>Staff</th>
                                                <th style={{ width: "110px" }}>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {markedList.length === 0 ? (
                                                <tr>
                                                    <td colSpan="3" className="text-center">
                                                        No attendance marked yet
                                                    </td>
                                                </tr>
                                            ) : (
                                                markedList.map((s, i) => {
                                                    const t = marked[s.id].attendance_type;
                                                    const meta = TYPES.find((x) => x.value === t);
                                                    return (
                                                        <tr key={s.id}>
                                                            <td>{i + 1}</td>
                                                            <td>{s.name}</td>
                                                            <td>
                                                                <span
                                                                    className="badge"
                                                                    style={{
                                                                        backgroundColor: meta
                                                                            ? meta.color
                                                                            : "#6c757d",
                                                                    }}
                                                                >
                                                                    {TYPE_LABEL[t] || t}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarkAttendance;
